import { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const INTERACTIVE = 'a, button, [role="button"], label, input, textarea, select';

/**
 * CustomCursor — dot + ring, mix-blend-difference on dot.
 * Only renders on devices with a fine pointer (mouse). No-op on touch.
 * Position is driven by motion values so mouse movement never re-renders React.
 */
const CustomCursor = () => {
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const hasFineMouse =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: fine) and (hover: hover)').matches;

  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const dotX = useSpring(x, { stiffness: 900, damping: 45, mass: 0.08 });
  const dotY = useSpring(y, { stiffness: 900, damping: 45, mass: 0.08 });
  const ringX = useSpring(x, { stiffness: 200, damping: 28, mass: 0.6 });
  const ringY = useSpring(y, { stiffness: 200, damping: 28, mass: 0.6 });

  useEffect(() => {
    if (!hasFineMouse) return;

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setIsVisible(true);
    };
    const onOver = (e) => setIsPointer(!!e.target.closest?.(INTERACTIVE));
    const onLeave = () => setIsVisible(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseover', onOver, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', onOver);
      document.documentElement.removeEventListener('mouseleave', onLeave);
    };
  }, [hasFineMouse, x, y]);

  if (!hasFineMouse) return null;

  const dot = isPointer ? 10 : 8;
  const ring = isPointer ? 44 : 26;

  return (
    <>
      {/* Inner dot — mix-blend-difference inverts against light elements */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full bg-white mix-blend-difference"
        style={{ x: dotX, y: dotY, translateX: '-50%', translateY: '-50%' }}
        animate={{ width: dot, height: dot, opacity: isVisible ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Outer ring — follows with lag */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9998] rounded-full"
        style={{
          x: ringX,
          y: ringY,
          translateX: '-50%',
          translateY: '-50%',
          border: '1px solid rgba(255,255,255,0.35)',
        }}
        animate={{
          width: ring,
          height: ring,
          opacity: isVisible ? (isPointer ? 0.65 : 0.3) : 0,
        }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      />
    </>
  );
};

export default CustomCursor;
